import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { classService, getErrorMessage, lessonManagementService, lessonService, lessonThumbnailSrc, submissionService } from '../services/api';
import type { Class, Difficulty, LessonAssignment, LessonStatus, LessonSummary } from '../types/api';
import AppNavBar from '../components/AppNavBar';
import StatusBadge from '../components/StatusBadge';
import DifficultyBadge from '../components/DifficultyBadge';
import ReadinessBadge from '../components/ReadinessBadge';
import PlanLine from '../components/PlanLine';
import { describeSubmissionWindow } from '../utils/format';
import {
  AlertCircle,
  ArrowRight,
  Plus,
  BookOpen,
  ChevronDown,
  CalendarClock,
  FlaskConical,
  Users,
} from 'lucide-react';
import { EmptyState, LoadingState, Modal, PageHeader } from '../components/ui';

interface ClassLesson extends LessonSummary {
  class_id: number;
  class_name: string;
  order_index: number;
  class_order_index: number;
  assignment: LessonAssignment;
  list_key: string;
}

// Lesson cards: one column on phones, then two and three.
const LESSON_GRID = 'grid gap-4 sm:grid-cols-2 xl:grid-cols-3';

// Every student can open every official lesson (LES-*), in a class or not: the library lists the ones their classes
// don't assign, which run on their own simulation time and can't be submitted (?demo=1). This one is the suggested
// first lesson.
const isDemoLesson = (lesson: LessonSummary) => /inverted pendulum/i.test(lesson.title);
const DIFFICULTY_ORDER: Record<Difficulty, number> = { easy: 0, medium: 1, hard: 2 };
const DIFFICULTY_FILTERS: Array<{ id: 'all' | Difficulty; label: string }> = [
  { id: 'all', label: 'All' },
  { id: 'easy', label: 'Easy' },
  { id: 'medium', label: 'Medium' },
  { id: 'hard', label: 'Hard' },
];

const StudentLessonCard: React.FC<{
  lesson: LessonSummary;
  badge: React.ReactNode;
  status: LessonStatus | undefined;
  footer?: React.ReactNode;
  onOpen: () => void;
}> = ({ lesson, badge, status, footer, onOpen }) => {
  const thumbnail = lessonThumbnailSrc(lesson);
  return (
    <div
      role="link"
      tabIndex={0}
      className="card-interactive group flex flex-col overflow-hidden"
      onClick={onOpen}
      onKeyDown={(event) => { if (event.key === 'Enter') onOpen(); }}
    >
      {thumbnail && (
        <div className="aspect-[16/9] w-full overflow-hidden border-b border-gray-200 dark:border-gray-800 bg-gray-100 dark:bg-gray-800">
          <img
            src={thumbnail}
            alt=""
            loading="lazy"
            className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-[1.03]"
          />
        </div>
      )}
      <div className="flex flex-1 flex-col p-4 sm:p-5">
        <div className="flex flex-wrap items-center gap-1.5 mb-2.5 min-w-0">{badge}<DifficultyBadge difficulty={lesson.difficulty} /><ReadinessBadge readiness={lesson.readiness} /></div>
        <h3 className="text-base sm:text-lg font-semibold leading-snug tracking-tight text-gray-900 dark:text-white group-hover:text-blue-700 dark:group-hover:text-blue-300 transition-colors">{lesson.title}</h3>
        <p className="mt-1 text-sm text-gray-600 dark:text-gray-400 line-clamp-2">{lesson.description}</p>
        {footer}
        <div className="mt-auto pt-4 flex items-center justify-between gap-2">
          <StatusBadge status={status || 'not_started'} size="sm" />
          <span className="inline-flex items-center gap-1 text-sm font-medium text-blue-700 dark:text-blue-300" aria-hidden="true">
            Open <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5" />
          </span>
        </div>
      </div>
    </div>
  );
};

const StudentDashboard: React.FC = () => {
  const [classes, setClasses] = useState<Class[]>([]);
  const [classFilter, setClassFilter] = useState<string>('all');
  const [classLessons, setClassLessons] = useState<ClassLesson[]>([]);
  const [libraryLessons, setLibraryLessons] = useState<LessonSummary[]>([]);
  const [difficultyFilter, setDifficultyFilter] = useState<'all' | Difficulty>('all');
  const [lessonStatuses, setLessonStatuses] = useState<Record<number, LessonStatus>>({});
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [showJoinClass, setShowJoinClass] = useState(false);
  const [inviteCode, setInviteCode] = useState('');
  const [joinError, setJoinError] = useState('');
  const navigate = useNavigate();

  const loadDashboardData = useCallback(async () => {
    try {
      setLoading(true);
      setLoadError('');

      const [enrolledClasses, officialLessons] = await Promise.all([
        classService.getClasses(),
        // For students: the official lessons, every one of which they may open.
        lessonService.list().catch(() => [] as LessonSummary[]),
      ]);
      setClasses(enrolledClasses);

      // Per class: its lessons (custom ones included) and the assignments (order, deadline). One class failing
      // doesn't hide the others.
      const perClass = await Promise.all(
        enrolledClasses.map(async (classData, classIndex) => {
          try {
            const [lessons, assignments] = await Promise.all([
              lessonService.listForClass(classData.id),
              lessonManagementService.getClassLessons(classData.id),
            ]);
            return { classData, classIndex, lessons, assignments };
          } catch (error) {
            console.error(`Error loading the lessons of class ${classData.id}:`, error);
            return { classData, classIndex, lessons: [] as LessonSummary[], assignments: [] as LessonAssignment[] };
          }
        })
      );

      const aggregatedLessons: ClassLesson[] = [];
      perClass.forEach(({ classData, classIndex, lessons, assignments }) => {
        // Assignments come filtered to the visible ones for students, so they decide what is listed.
        assignments.forEach((assignment) => {
          const lesson = lessons.find((item) => item.id === assignment.lesson_id);
          if (!lesson) {
            return;
          }
          aggregatedLessons.push({
            ...lesson,
            class_id: classData.id,
            class_name: classData.name,
            order_index: assignment.order_index,
            class_order_index: classIndex,
            assignment,
            list_key: `${classData.id}-${lesson.id}`,
          });
        });
      });

      aggregatedLessons.sort((a, b) => {
        if (a.class_order_index !== b.class_order_index) {
          return a.class_order_index - b.class_order_index;
        }
        if (a.order_index !== b.order_index) {
          return a.order_index - b.order_index;
        }
        return a.title.localeCompare(b.title);
      });

      setClassLessons(aggregatedLessons);

      const assignedIds = new Set(aggregatedLessons.map((lesson) => lesson.id));
      const library = officialLessons
        .filter((lesson) => lesson.lesson_type === 'official' && lesson.is_active && !assignedIds.has(lesson.id))
        .sort((a, b) => Number(isDemoLesson(b)) - Number(isDemoLesson(a))
          || (DIFFICULTY_ORDER[a.difficulty] ?? 1) - (DIFFICULTY_ORDER[b.difficulty] ?? 1)
          || a.title.localeCompare(b.title));
      setLibraryLessons(library);

      const uniqueLessonIds = Array.from(new Set([
        ...aggregatedLessons.map((lesson) => lesson.id),
        ...library.map((lesson) => lesson.id),
      ]));
      const statusEntries = await Promise.all(
        uniqueLessonIds.map(async (lessonId) => {
          try {
            return [lessonId, await submissionService.lessonStatus(lessonId)] as const;
          } catch {
            return [lessonId, 'not_started' as LessonStatus] as const;
          }
        })
      );

      const statuses: Record<number, LessonStatus> = {};
      statusEntries.forEach(([lessonId, status]) => {
        statuses[lessonId] = status;
      });
      setLessonStatuses(statuses);

      setClassFilter((currentFilter) => {
        if (currentFilter === 'all') {
          return 'all';
        }

        const availableFilterIds = new Set(aggregatedLessons.map((lesson) => String(lesson.class_id)));
        return availableFilterIds.has(currentFilter) ? currentFilter : 'all';
      });
    } catch (error) {
      console.error('Error loading dashboard data:', error);
      setLoadError(getErrorMessage(error, 'Your lessons could not be loaded.'));
      setClasses([]);
      setClassLessons([]);
      setLibraryLessons([]);
      setLessonStatuses({});
      setClassFilter('all');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadDashboardData();
  }, [loadDashboardData]);

  const handleJoinClass = async () => {
    setJoinError('');
    try {
      await classService.joinClassByInviteCode(inviteCode);
      setShowJoinClass(false);
      setInviteCode('');
      setJoinError('');
      setClassFilter('all');
      await loadDashboardData();
    } catch (error) {
      setJoinError(getErrorMessage(error, 'Failed to join class'));
    }
  };

  const filteredLessons = useMemo(() => {
    if (classFilter === 'all') {
      return classLessons;
    }

    return classLessons.filter((lesson) => String(lesson.class_id) === classFilter);
  }, [classFilter, classLessons]);

  const classFilterOptions = useMemo(() => {
    const options: Array<{ id: string; name: string }> = [];
    const seen = new Set<string>();

    classLessons.forEach((lesson) => {
      const optionId = String(lesson.class_id);
      if (seen.has(optionId)) {
        return;
      }

      seen.add(optionId);
      options.push({ id: optionId, name: lesson.class_name });
    });

    return options;
  }, [classLessons]);

  const isFilteredView = classFilter !== 'all';

  const closeJoinClass = useCallback(() => {
    setShowJoinClass(false);
    setInviteCode('');
    setJoinError('');
  }, []);

  const visibleLibrary = difficultyFilter === 'all' ? libraryLessons : libraryLessons.filter((lesson) => lesson.difficulty === difficultyFilter);
  const librarySection = libraryLessons.length > 0 && (
    <section className={classLessons.length > 0 || classes.length === 0 ? 'mt-10' : ''} aria-labelledby="lesson-library-heading">
      <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
        <div className="min-w-0">
          {/* h2, not h3: lesson titles are the page's h3s (the student E2E test opens the first one) */}
          <h2 id="lesson-library-heading" className="section-title">
            {classLessons.length > 0 ? 'More lessons' : 'Lesson library'}
          </h2>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            Open any lesson and run it on your own simulation time. Submitting work needs a class that assigns the lesson.
          </p>
        </div>
        <div className="tabs sm:w-fit shrink-0" role="group" aria-label="Filter by difficulty">
          {DIFFICULTY_FILTERS.map((option) => (
            <button
              key={option.id}
              type="button"
              aria-pressed={difficultyFilter === option.id}
              onClick={() => setDifficultyFilter(option.id)}
              className={`tab ${difficultyFilter === option.id ? 'tab-active' : ''}`}
            >
              {option.label}
            </button>
          ))}
        </div>
      </div>
      {visibleLibrary.length === 0 ? (
        <EmptyState title={`No ${difficultyFilter} lessons yet.`} />
      ) : (
        <div className={LESSON_GRID}>
          {visibleLibrary.map((lesson) => (
            <StudentLessonCard
              key={`library-${lesson.id}`}
              lesson={lesson}
              status={lessonStatuses[lesson.id]}
              badge={isDemoLesson(lesson) ? (
                <span className="badge badge-blue">
                  <FlaskConical /> Start here
                </span>
              ) : null}
              onOpen={() => navigate(`/lesson/${lesson.id}?demo=1`)}
            />
          ))}
        </div>
      )}
    </section>
  );

  return (
    <div className="page-shell">
      <AppNavBar title="Student Portal" roleLabel="Student" />

      <div className="page-container">
        <PageHeader
          title="My Lessons"
          subtitle="Continue your robotics learning journey"
          actions={
            // The main action only while there is no class yet; then the lessons are.
            <button onClick={() => setShowJoinClass(true)} className={`btn btn-lg ${classes.length === 0 && !loading ? 'btn-primary' : 'btn-secondary'}`}>
              <Plus />
              <span>Join a Class</span>
            </button>
          }
        >
          <PlanLine />
        </PageHeader>

        {showJoinClass && (
          <Modal title="Join a Class" description="Get the invite code from your teacher" onClose={closeJoinClass}>
            <form
              onSubmit={(event) => {
                event.preventDefault();
                handleJoinClass();
              }}
            >
              <label htmlFor="join-invite-code" className="label">Invite Code</label>
              <input
                id="join-invite-code"
                type="text"
                value={inviteCode}
                onChange={(e) => setInviteCode(e.target.value.toUpperCase())}
                className="input py-3 uppercase font-mono text-lg tracking-widest text-center"
                placeholder="ABC12345"
                maxLength={8}
                autoFocus
                autoComplete="off"
              />
              {joinError && (
                <div className="alert alert-error mt-4" role="alert">
                  <AlertCircle />
                  <span>{joinError}</span>
                </div>
              )}
              <div className="modal-actions">
                <button type="button" onClick={closeJoinClass} className="btn btn-secondary">
                  Cancel
                </button>
                <button type="submit" disabled={!inviteCode.trim()} className="btn btn-primary">
                  Join Class
                </button>
              </div>
            </form>
          </Modal>
        )}

        {loading ? (
          <LoadingState label="Loading lessons..." />
        ) : loadError ? (
          <EmptyState
            icon={<AlertCircle />}
            title={<span className="text-red-700 dark:text-red-300">{loadError}</span>}
            action={<button onClick={loadDashboardData} className="btn btn-primary">Retry</button>}
          />
        ) : (
          <>
            {classes.length === 0 ? (
              <EmptyState
                icon={<Users />}
                title="You are not enrolled in any classes yet"
                action={
                  <button onClick={() => setShowJoinClass(true)} className="btn btn-secondary">
                    <Plus />
                    Enter Invite Code
                  </button>
                }
              >
                Use the invite link or code from your professor to join a class{libraryLessons.length > 0 ? ', or pick any lesson below' : ''}.
              </EmptyState>
            ) : (
              <section aria-labelledby="class-lessons-heading">
                <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                  <h2 id="class-lessons-heading" className="section-title">
                    From your classes <span className="ml-1 text-sm font-normal text-gray-500 dark:text-gray-400">{filteredLessons.length} {filteredLessons.length === 1 ? 'lesson' : 'lessons'}</span>
                  </h2>
                  {classFilterOptions.length > 1 && (
                    <div className="flex items-center gap-2 min-w-0">
                      <label htmlFor="student-class-filter" className="text-sm font-medium text-gray-600 dark:text-gray-400 shrink-0">Class</label>
                      <div className="relative min-w-0 flex-1 sm:w-60">
                        <select
                          id="student-class-filter"
                          value={classFilter}
                          onChange={(e) => setClassFilter(e.target.value)}
                          className="select"
                        >
                          <option value="all">All Classes</option>
                          {classFilterOptions.map((option) => (
                            <option key={option.id} value={option.id}>
                              {option.name}
                            </option>
                          ))}
                        </select>
                        <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
                      </div>
                    </div>
                  )}
                </div>
                {filteredLessons.length === 0 ? (
                  <EmptyState icon={<BookOpen />} title="No lessons available">
                    {isFilteredView
                      ? 'No visible lessons in the selected class.'
                      : 'No visible lessons across your enrolled classes yet.'}
                  </EmptyState>
                ) : (
                  <div className={LESSON_GRID}>
                    {filteredLessons.map((lesson) => {
                      const submissionWindow = describeSubmissionWindow(lesson.assignment);
                      return (
                        <StudentLessonCard
                          key={lesson.list_key}
                          lesson={lesson}
                          status={lessonStatuses[lesson.id]}
                          badge={<span className="badge badge-gray normal-case tracking-normal max-w-full truncate">{lesson.class_name}</span>}
                          footer={submissionWindow.label && (
                            <p className={`mt-3 flex items-center gap-1.5 text-xs font-medium ${submissionWindow.open
                              ? 'text-gray-600 dark:text-gray-400'
                              : 'text-amber-700 dark:text-amber-400'
                              }`}>
                              <CalendarClock className="w-3.5 h-3.5 shrink-0" />
                              {submissionWindow.label}
                            </p>
                          )}
                          onOpen={() => navigate(`/lesson/${lesson.id}?class=${lesson.class_id}`)}
                        />
                      );
                    })}
                  </div>
                )}
              </section>
            )}
            {librarySection}
          </>
        )}
      </div>
    </div>
  );
};

export default StudentDashboard;
